import React from 'react';
import Uppy from '@uppy/core';
import Dashboard, { DashboardOptions } from '@uppy/dashboard';
import Box from '@uppy/box';
import Dropbox from '@uppy/dropbox';
import GoogleDrive from '@uppy/google-drive';
import TusPatchedPlugin from '../plugins/TusPatch';
import Url from '@uppy/url';
import ImageEditor from '@uppy/image-editor';
import DragDrop from '@uppy/drag-drop';
import { FC, useEffect, useMemo, useCallback } from 'react';
import { type UppyUploaderProps } from '@components/common/uppy/types/UppyTypes';
import { sendAction } from '@helpers/datadog-rum';
import {
  transformUppyUCSResult,
  COMPANION_SANDBOX_URL,
  COMPANION_ALLOWED_HOSTS,
} from '@helpers/uppy';

import '@uppy/core/dist/style.css';
import '@uppy/dashboard/dist/style.css';
import '@uppy/image-editor/dist/style.min.css';
import '@uppy/drag-drop/dist/style.css';

import '@components/common/uppy/styles/uppy_uploader.scss';
import { Result } from '@mocks/uppy-transloadit';
import FileSearchPlugin from '../plugins/Search';

interface BrandfolderTusOptions {
  bearerToken?: string;
  isFl2?: boolean;
  refreshToken?: string;
  version?: string;
  tusUrl?: string;
}

interface ErrorArray {
  error: string[];
}

interface ErrorObject {
  error: string;
}

interface FetchTusSignedUrlOptionsV2 extends Pick<BrandfolderTusOptions, 'bearerToken'> {
  tusUrl: string;
}

interface TusSignedURLsResponse {
  signed_url: string;
}

export const fetchTusSignedUrlV2 = async (options: FetchTusSignedUrlOptionsV2): Promise<string> => {
  try {
    const { bearerToken, tusUrl } = options;

    const response = await fetch(`${tusUrl}/signed_url`, {
      headers: {
        Accept: 'application/json',
        Authorization: `Bearer ${bearerToken}`
      },
      method: 'GET'
    });

    if (response.ok) {
      const signedUrls = (await response.json()) as TusSignedURLsResponse;
      return signedUrls?.signed_url || '';
    } else {
      const e = (await response.json()) as ErrorArray | ErrorObject;
      throw new Error(Array.isArray(e?.error) ? e.error[0] : e?.error || 'Unknown error');
    }
  } catch (error) {
    console.error(`fetchTusSignedUrlV2`, { error });
    return '';
  }
};

export const UppyUcsUploader: FC<UppyUploaderProps> = ({
  handleUpload,
  uniqIdentifier,
  button = false,
  closeOnComplete = false,
  locale = {
    strings: {
      dropPaste: 'Drag file(s) here or %{browse}',
      dropHereOr: 'Drag file(s) here or %{browse}',
      browse: 'click to browse'
    }
  },
  restrictions = {},
  endpoint,
  template = 'file_ingest',
  trigger = undefined,
  onTriggerClickHandler,
  ucsSdk,
  inline = false,
  guestUpload,
}) => {

  // useMemo to maintain single instance of Uppy per component lifecycle
  const uppy = useMemo(() => new Uppy({
    allowMultipleUploadBatches: true,
    id: `${uniqIdentifier}-uppy`,
    restrictions: { maxNumberOfFiles: 3000, ...restrictions },
    locale,
  }), [locale, restrictions, uniqIdentifier]);

  // Create handler function that can be passed to parent components
  const handleTriggerClick = useCallback((event: React.MouseEvent) => {
    event.preventDefault();
    const dashboard = uppy.getPlugin('Dashboard') as Dashboard;
    if (dashboard) {
      dashboard.openModal();
    }
  }, [uppy]);

  // Directly assign handler to ref if provided (no useEffect needed)
  // Refs are mutable and can be assigned during render without causing issues
  if (onTriggerClickHandler) {
    onTriggerClickHandler.current = handleTriggerClick;
  }

  // Cleanup: clear ref on unmount
  useEffect(() => {
    return () => {
      if (onTriggerClickHandler) {
        onTriggerClickHandler.current = null;
      }
    };
  }, [onTriggerClickHandler]);

  const templateName = template.replace(/-/g, '_');
  const dashboardOptions: DashboardOptions = {
    browserBackButtonClose: button,
    closeAfterFinish: button && !inline, // Cannot close inline dashboards
    closeModalOnClickOutside: button,
    inline,
    proudlyDisplayPoweredByUppy: false,
    showProgressDetails: true,
    trigger,
    // Only set target for inline mode, and make sure it's valid
    ...(inline && { target: `#${uniqIdentifier}-dashboard` }),
    width: inline ? '100%' : 10,
    showSelectedFiles: true,
    showRemoveButtonAfterComplete: true,
    // Ensure it doesn't auto-open file picker
    note: 'Select files from your computer or connect to external services'
  };

  const handleError = (file, error): void => {
    sendAction('Error Uploading File to Tus', {
      tags: {},
      extra: { file, error }
    });
  };

  useEffect(() => {
    uppy.use(TusPatchedPlugin, {
      endpoint: `https://${endpoint}/v2/files/`,
      limit: 10, // no of uploads going at the same time
      onBeforeRequest: async (req, file) => {
        try {
          const tusUrl = req.getURL();
          if (!tusUrl) {
            uppy.log(`[] tusUrl not defined`, 'error');
            return;
          }
          const identityToken = await ucsSdk.getIdentityToken()
          req.setHeader('Authorization', `Bearer ${identityToken}`);

          /**
           * NOTE: `tusId` will not be defined the first time `onBeforeRequest` runs,
           * so we wait to fetch until the second time `onBeforeRequest` runs.
           * This isn't an error, this is just how the Tus plugin seems to work.
           */
          const tusId = tusUrl.split('/').at(-1);
          if (!tusId) return;

          /**
           * NOTE: `brandfolderTusUrl` is the file access URL that is used to POST the asset to BF.
           * It technically doesn't have anything to do with Tus (it's really Google Cloud Storage).
           * But, we're keeping the variable name `brandfolderTusUrl` for searchability in GitHub/Sourcegraph
           * and so that we don't have to update downstream FE code.
           */
          const brandfolderTusUrl = await fetchTusSignedUrlV2({
            bearerToken: identityToken,
            tusUrl
          });

          if (!brandfolderTusUrl) {
            uppy.log(`[] brandfolderTusUrl not defined`, 'error');
            return;
          }
          uppy.setFileMeta(file.id, { brandfolderTusUrl });
        } catch (err) {
          console.error(err);
        }
      },
      chunkSize: 2 * 1024 * 1024 // 2 MB
    }).on('upload-error', (file, body) => {
      handleError(file, body);
    })
      .on('transloadit:complete', (assembly) => {
        const files = assembly.results[':original']
          || assembly.results[templateName];
        handleUpload(files);
      })
      .on('complete', async (result) => {
        // Get identity token once for all files
        const identityToken = await ucsSdk.getIdentityToken();

        // Filter files that need signed URLs (3rd party uploads only)
        const filesNeedingSignedUrls = (result?.successful || []).filter(
          file => file.source !== "Dashboard"
        );

        // Fetch all signed URLs in parallel
        await Promise.all(
          filesNeedingSignedUrls.map(async (file) => {
            const tusUrl = file?.response?.uploadURL || '';
            const brandfolderTusUrl = await fetchTusSignedUrlV2({
              bearerToken: identityToken,
              tusUrl
            });
            file.meta = { ...file.meta, brandfolderTusUrl };
          })
        ).catch((error: unknown) => {
          console.error('Failed to fetch signed URLs', error);
        });

        // Transform result data to desired format using helper function
        const transformedFiles = await transformUppyUCSResult(result);

        handleUpload(transformedFiles as Result[]);
        if (closeOnComplete) {
          uppy.close();
        } else {
          uppy.reset();
        }
      })
      .use(Dashboard, dashboardOptions)
      .use(ImageEditor, {
        target: Dashboard
      })
      .use(FileSearchPlugin, {
        id: 'FileSearchPlugin',
      })
      .use(Box, {
        companionUrl: COMPANION_SANDBOX_URL,
        companionAllowedHosts: COMPANION_ALLOWED_HOSTS,
        target: Dashboard
      })
      .use(Url, {
        companionUrl: COMPANION_SANDBOX_URL,
        target: Dashboard
      })
      .use(Dropbox, {
        companionUrl: COMPANION_SANDBOX_URL,
        companionAllowedHosts: COMPANION_ALLOWED_HOSTS,
        target: Dashboard
      })
      .use(GoogleDrive, {
        companionUrl: COMPANION_SANDBOX_URL,
        companionAllowedHosts: COMPANION_ALLOWED_HOSTS,
        target: Dashboard
      })
      .use(DragDrop, {
        target: trigger,
      });


    const targetElement = document.querySelector(trigger);
    if (targetElement) {
      targetElement.addEventListener('click', (event) => {
        event.preventDefault();
        const dashboard = uppy.getPlugin('Dashboard') as Dashboard;
        if (dashboard) {
          dashboard.openModal();
        }
      });
    }

    function updateDashboardClass() {
      const dashboardEl = document.querySelector('.uppy-Dashboard-files');
      if (!dashboardEl) return;
      const items = dashboardEl.querySelectorAll('.uppy-Dashboard-Item');
      dashboardEl.classList.toggle('single-item', items.length === 1);
    }

    // Attach Uppy event listeners
    uppy.on('file-added', () => {
      // Delay to give Uppy time to render DOM for new file
      requestAnimationFrame(updateDashboardClass);

      // Automatically open the Dashboard modal when files are added
      const dashboard = uppy.getPlugin('Dashboard') as Dashboard;
      if (dashboard && !dashboard.isModalOpen()) {
        dashboard.openModal();
      }
    });

    uppy.on('file-removed', () => {
      requestAnimationFrame(updateDashboardClass);
    });

    // Observe DOM until dashboard is mounted, then run initial update
    const observer = new MutationObserver(() => {
      const dashboardEl = document.querySelector('.uppy-Dashboard-files');
      if (dashboardEl) {
        updateDashboardClass();
        observer.disconnect(); // Stop observing once found
      }
    });

    observer.observe(document.body, { childList: true, subtree: true });

    return () => {
      uppy.off('file-added', updateDashboardClass);
      uppy.off('file-removed', updateDashboardClass);
      observer.disconnect();
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const dashboard: Dashboard = uppy.getPlugin('Dashboard');
  if (dashboard && !inline) {
    // Only manually render for modal dashboards, inline dashboards render automatically
    dashboard.render({});
  }

  return (inline || !trigger) ? (
    <div id={`${uniqIdentifier}-dashboard`} />
  ) : null;
};
